/* Storystir personalizer — the product page's live preview, review sheet and bag.
 *
 * PANEL-V1 (Oct 2026). The page opens like the home hero: slide 1 is the book standing with an
 * example name on it and a gold "Try their name" pill; under the price ONE button, "Personalize",
 * opens THE PANEL (a bottom sheet on a phone, a drawer on a desktop) which holds the form: their
 * cover live at the top (the instant cover, then the real render), First name (the one required
 * field), Last name (optional), the dedication and the sign-off pre-filled with a default that
 * prints unless changed (folded: "We'll print this unless you change it. Edit"), the occasion
 * (blank), each with a Done button when open. "Preview their edition" (outlined until there is a first name, then
 * gold) opens the review sheet over the panel, with "Back to edit" at its top and foot; "Add to cart" is pinned
 * at the panel's foot under the same rule as before (their render on screen for what is typed).
 * The quiet link under the button chooses the Library edition (the same cart line as before)
 * without the panel. A phone gets a sticky bar once the photograph has scrolled away.
 * Clarity: the smart events "Opened panel", "Used personalizer", "Preview viewed", "Add to cart";
 * anything that can carry a name is marked data-clarity-mask in the markup.
 * The cart line's properties are exactly as they were (see properties() below).
 *
 * (The flow before panel-v1, still true inside the panel:)
 *
 * THE FLOW (Tom, 22 Sept 2026): the preview is a step before buying, not a
 * side effect you can skip.
 *
 *   type   → the render service (/preview) swaps the product images for the
 *            real boards and pages as the customer types (600ms after the last
 *            key). On a phone a small photograph of their front cover follows
 *            them, pinned top right of the form, with a thin line while it
 *            renders; tapping it opens the review sheet.
 *   review → "Preview their book" (on once the four required fields have values;
 *            until then a helper line says what is missing) opens the review
 *            sheet: their named cover photographs, their pages, the words as
 *            typed, and a box to tick that the spelling has been checked.
 *   bag    → only the sheet's "Add to cart" adds the line — with every property,
 *            plus _proof_confirmed and a timestamp — and opens the bag drawer:
 *            Checkout, or another book. The form never submits; there is no
 *            direct checkout on this page. The Library edition has nothing to
 *            review and goes straight from its button to the bag.
 *
 * Property names (the contract with app/shopify.py — change both or neither):
 *   Recipient name · Surname · Dedication · Occasion · Sign-off · Edition · _slug · _preview
 *   plus _proof_confirmed ("yes") and _proof_confirmed_at (ISO time) on a
 *   personalized line — on the order, for a spelling dispute. Edition is
 *   "Personalized edition" or "Library edition".
 *
 * Images: data-view names match the render service's preview manifest; a view
 * can appear more than once (slide, thumbnail, the phone's pinned cover) and all
 * swap. Nothing is ever drawn onto an image here: every pixel comes from the
 * service. Requests are never cancelled: one at a time, the latest typing waits
 * its turn, every finished render is shown as it lands.
 *
 * Markup contract: see sections/personaliser.liquid. Nothing here depends on
 * the theme's CSS classes except the data-attributes and the is-library flag.
 */
(function () {
  const root = document.querySelector('[data-personaliser]');
  if (!root) return;
  const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------ gallery */
  const track = root.querySelector('[data-track]');
  let arrange = () => {};
  if (track) {
    const captionEl = root.querySelector('[data-caption-text]');
    const nav = root.querySelector('[data-gnav]');
    // The shared component (assets/ss-gallery.js): the same arrows, dots and count as the
    // home hero's demo. The thumbnails (desktop) and the caption follow whatever it marks.
    const gal = window.ssGallery(track, nav, {
      slide: '[data-slide]',
      label: slide => 'Show ' + (slide.dataset.caption || '').toLowerCase().replace(/^the /, 'the '),
      onMark: (i, s) => {
        if (!s) return;
        root.querySelectorAll('[data-thumb]').forEach(b => b.setAttribute('aria-current', String(b.dataset.thumb === s.dataset.slide)));
        if (captionEl) captionEl.textContent = s.dataset.caption || '';
      },
    });
    const live = gal.slides, go = gal.go;
    // A tap or click on the photograph shows the next one, and after the last the first again
    // (12 Oct 2026: visitors tapped the photo expecting it to move). The arrows, dots and swipe
    // are the component's own and unchanged; a pinch is not a tap, so zoom is untouched.
    track.addEventListener('click', e => {
      if (!e.target.closest('[data-slide] img')) return;
      const n = live().length;
      if (n < 2) return;
      const i = gal.index();
      go(i >= n - 1 ? 0 : i + 1);
    });
    /* A photograph that is missing: drop its slide and thumbnail. (A title with no
       photographs at all is not ready and is not shown on the site.) */
    const dropped = new Set();
    function drop(slide) {
      dropped.add(slide.dataset.slide);
      slide.hidden = true;
      const li = root.querySelector('li[data-key="' + slide.dataset.slide + '"]');
      if (li) li.hidden = true;
      gal.rebuild();
    }
    // (panel-v1) the example names on slide 1 and the hidden picture of their own book are not the slide's
    // photograph: one of them failing never drops the slide
    track.querySelectorAll('[data-slide] img').forEach(img => {
      if (img.closest('[data-h1-names]') || img.classList.contains('ss-h1__theirs')) return;
      const slide = img.closest('[data-slide]');
      if (img.complete && img.naturalWidth === 0 && img.getAttribute('src')) drop(slide);
      img.addEventListener('error', () => { if (img.dataset.swapped !== '1') drop(slide); });
    });
    // The same set as the home demo (Tom, 24 Sept): the three photographs and the
    // pages that carry a name; 3D only, never a flat print. The dedication page
    // (data-when="named") joins once a render has landed, since the book's own
    // carries only the Storystir mark. A slide whose sample image is missing stays
    // out until a render gives it a real one; then the dots are rebuilt.
    arrange = () => {
      root.querySelectorAll('[data-slide], li[data-key]').forEach(el => {
        const key = el.dataset.slide || el.dataset.key;
        const img = el.querySelector('img');
        const named = !!(img && img.dataset.swapped === '1');
        const slide = el.dataset.slide ? el : track.querySelector('[data-slide="' + key + '"]');
        const onlyNamed = !!(slide && slide.dataset.when === 'named');
        el.hidden = (dropped.has(key) && !named) || (onlyNamed && !named);
      });
      gal.rebuild();
      heroSync();
    };
    root.querySelectorAll('[data-thumb]').forEach(b => b.addEventListener('click', () => {
      go(live().findIndex(s => s.dataset.slide === b.dataset.thumb));
    }));
  }

  /* ------------------------------------------------------------ preview */
  const API = root.dataset.api;              // the render service
  const KEY = root.dataset.key || '';        // public shared key (rate limiting only)
  const slug = root.dataset.slug;
  const UNNAMED = 'plain';                      // the render service's API value for the copy without personalization. Code only: the shop's word is "Library edition".
  const form = root.querySelector('form.ss-form');
  const f = {
    first: root.querySelector('[name="properties[Recipient name]"]'),
    last: root.querySelector('[name="properties[Surname]"]'),
    dedication: root.querySelector('[name="properties[Dedication]"]'),
    signed_off: root.querySelector('[name="properties[Sign-off]"]'),
    occasion: root.querySelector('[name="properties[Occasion]"]'),
    preview: root.querySelector('[name="properties[_preview]"]'),
  };
  const personal = [f.first, f.last, f.dedication, f.signed_off, f.occasion].filter(Boolean);
  // The edition (panel-v1): the radios left with the inline form. "Personalized edition" unless the
  // Library edition link (or ?edition=library) chose the "Library edition"; the values are the cart's, unchanged.
  const PERSONAL = 'Personalized edition', LIBRARY = 'Library edition';
  let ed = PERSONAL;
  const edInput = root.querySelector('[data-edition]');
  const views = Array.from(document.querySelectorAll('img[data-view]'));   // the photographs, the page slides, their thumbnails, the pinned cover
  // the WebP <source> beside a picture (sections/personaliser.liquid, 11 Oct 2026): the plain photograph's copies
  const webpOf = el => (el.parentElement && el.parentElement.tagName === 'PICTURE' ? el.parentElement.querySelector('source') : null);
  views.forEach(el => {                                    // the non-personalized images, to restore
    el.dataset.original = el.getAttribute('src') || el.dataset.ssSrc || '';   // a slide not yet near holds its address in data-ss-src
    const w = webpOf(el);
    if (w) el.dataset.originalSet = w.getAttribute('srcset') || w.dataset.ssSrcset || '';
  });
  const status = root.querySelector('[data-status]');
  const problems = root.querySelector('[data-problems]');
  const submit = root.querySelector('[data-add]');
  const addLabel = root.querySelector('[data-add-label]');
  const addDirect = root.querySelector('[data-add-direct]');   // round of 3 Oct: Add to cart beside Preview, once there is a name
  const printed = root.querySelector('[data-printed]');        // "Printed as: First Surname. From Sign-off."
  const helper = root.querySelector('[data-helper]');
  const count = root.querySelector('[data-count]');
  const busyEl = root.querySelector('[data-busy]');
  const thumb = root.querySelector('[data-thumb-open]');
  const PRICE = root.dataset.price || '';
  // panel-v1: the buy box's one button and the Library edition, the panel's foot
  const personalizeBtn = root.querySelector('[data-personalize]');
  const classicBox = root.querySelector('[data-classic-box]');
  const classicAdd = root.querySelector('[data-classic-add]');
  const classicLink = root.querySelector('[data-classic-link]');
  const unclassicLink = root.querySelector('[data-unclassic-link]');
  const buyStatus = root.querySelector('[data-buy-status]');
  const footnote = root.querySelector('[data-panel-footnote]');
  // Clarity smart events (panel-v1). Clarity is the app embed (settings_data.json); without it, nothing.
  function clarityEvent(name, tag) {
    try {
      if (typeof window.clarity !== 'function') return;
      if (tag) window.clarity('set', tag[0], tag[1]);
      window.clarity('event', name);
    } catch (e) { /* tracking never gets in the way */ }
  }

  /* ------------------------------------------------------------ the instant cover (12 Oct 2026)
     Tom's iPhone waited 8 to 9 seconds for a cover. Now the title's own front photograph
     with an empty name line (tools/blank_labels.py, numbers in snippets/ss-instant.liquid)
     is ready before they ask, and their name is set over it in the label's face, size,
     colour and place the moment they do. It is only ever a stand-in: Add to cart waits for
     the real render, which fades in over it in exactly the same place as soon as it has loaded. */
  let instantMeta = null, instant = null;                   // instant: the numbers, once the picture and the face are loaded
  try { instantMeta = root.dataset.instant ? JSON.parse(root.dataset.instant) : null; } catch (e) { instantMeta = null; }
  const SVGNS = 'http://www.w3.org/2000/svg';
  function instantLoad() {
    if (!instantMeta || !instantMeta.img || instant) return;
    const im = new Image();
    im.decoding = 'async';
    im.src = instantMeta.img;
    const face = document.fonts && document.fonts.load ? document.fonts.load('500 40px "EB Garamond"') : Promise.resolve();
    Promise.all([im.decode ? im.decode() : new Promise((ok, no) => { im.onload = ok; im.onerror = no; }), face])
      .then(() => { instant = instantMeta; if (sheetIsOpen() && !sheetTrack.children.length) sheetFill(); panelCover(); })
      .catch(() => {});
  }
  function instantCover() {
    const m = instant;
    const svg = document.createElementNS(SVGNS, 'svg');
    svg.setAttribute('viewBox', '0 0 ' + m.w + ' ' + m.h);
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');      // = the slide's object-fit: contain
    svg.setAttribute('class', 'ss-instant');
    svg.setAttribute('data-instant', '');
    svg.setAttribute('aria-hidden', 'true');
    const pic = document.createElementNS(SVGNS, 'image');
    pic.setAttribute('href', m.img);
    pic.setAttribute('width', m.w); pic.setAttribute('height', m.h);
    const text = document.createElementNS(SVGNS, 'text');
    text.setAttribute('text-anchor', 'middle');
    text.setAttribute('fill', m.ink);
    text.setAttribute('font-family', '"EB Garamond", Georgia, serif');
    text.setAttribute('font-weight', '500');
    svg.append(pic, text);
    return svg;
  }
  // The name, set as cover-case.typ sets it: capitals, tracked 0.05em, at the label's size if it
  // fits the column, else as far down as 70%; past that, smaller still on one line (the real
  // render takes two lines there, and replaces this within seconds). Needs the svg on screen.
  function instantName(svg, name) {
    const m = instant, text = svg && svg.querySelector('text');
    if (!m || !text) return;
    // Measured untracked, the tracking added by sum: Safari's getComputedTextLength leaves
    // letter-spacing out and Chrome's counts it, so neither engine's number is used with it.
    const width = (t, sz) => {
      text.textContent = t;
      text.setAttribute('font-size', sz);
      text.setAttribute('letter-spacing', '0');
      return text.getComputedTextLength() + (Array.from(t).length - 1) * sz * 0.05;
    };
    const h = width('HHHHHH', 100);
    if (!(h > 0)) return;
    const base = 100 * m.hw / h, col = m.colem * base;
    const T = name.toUpperCase().replace(/-/g, '‑');
    let size = base;
    const full = width(T, base);
    if (full > col) {
      size = 0;
      for (const k of [0.95, 0.9, 0.85, 0.8, 0.75, 0.7]) { if (width(T, base * k) <= col) { size = base * k; break; } }
      if (!size) size = base * col / full;
    }
    width(T, size);
    text.setAttribute('letter-spacing', (size * 0.05).toFixed(2));
    text.setAttribute('x', (m.cx + size * 0.025).toFixed(1));
    text.setAttribute('y', m.base);
  }

  // Wake the render service and get this title ready (GET /warm), and fetch the blank cover,
  // once the page has loaded; again when they start on the name, in case the page sat open.
  let warmed = 0;
  function hello() {
    const now = Date.now();
    if (now - warmed < 60000) return;
    warmed = now;
    if (API && slug) fetch(API + '/warm/' + encodeURIComponent(slug), { mode: 'cors', credentials: 'omit' }).catch(() => {});
    instantLoad();
  }
  const helloSoon = () => setTimeout(hello, 400);
  if (document.readyState === 'complete') helloSoon(); else window.addEventListener('load', helloSoon, { once: true });

  let timer = null, busy = false, waiting = false, shownKey = '', last = null;   // last: the render on screen {key, urls, spec}
  let partial = null;                                        // the cover stage of the render in flight {key, urls} (12 Oct 2026)
  let behind = null;                                         // the page's own picture swaps, held while the sheet loads its pictures
  let coverFor = '';                                         // the name the real front photograph (data-view="shot_front") was asked for with
  let fullFor = '', fullClock = 0;                           // panel-v1: the words whose pages may be set while a name field has the focus

  if (form) form.addEventListener('submit', e => e.preventDefault());   // no direct checkout, ever
  /* (22 Oct 2026, the mobile panel fix) On a phone (up to 749px) the panel has no book: it opens straight to the
     fields, and nothing is rendered while they type. Their edition is made when they tap "Preview their edition"
     (sheetOpen asks for it), and the page's own photographs catch up once, when the panel closes. */
  const phoneMobile = () => window.matchMedia('(max-width: 749px)').matches;
  const panelBookless = () => {
    const p = root.querySelector('[data-panel]');
    return phoneMobile() && !!(p && !p.hidden && p.classList.contains('is-open'));
  };

  function edition() { return ed; }

  function spec() {
    const library = edition().toLowerCase().startsWith('library');   // the Library edition: the book without personalization
    return {
      slug,
      copy_type: library ? UNNAMED : 'personalised',
      first: library ? '' : (f.first?.value || '').trim(),
      last: library ? '' : (f.last?.value || '').trim(),
      dedication: library ? '' : (f.dedication?.value || '').trim(),
      signed_off: library ? '' : (f.signed_off?.value || '').trim(),
      occasion: library ? '' : (f.occasion?.value || '').trim(),
    };
  }

  function say(msg, bad) {
    if (!status) return;
    status.textContent = msg || '';
    status.classList.toggle('is-bad', !!bad);
  }

  // A named preview has no WebP copies: its picture's <source> is emptied so the <img> shows it; the original
  // puts the copies back. (A <source> with a srcset always wins over the <img>'s own src.)
  function setWebp(el, url) {
    const w = webpOf(el);
    if (!w) return;
    delete w.dataset.ssSrcset;
    if (url === el.dataset.original && el.dataset.originalSet) w.setAttribute('srcset', el.dataset.originalSet);
    else w.removeAttribute('srcset');
  }
  function setSrc(el, url) {
    if (!url) return;
    if (el.dataset.ssSrc !== undefined && url === el.dataset.original) return;   // never shown yet: it keeps waiting
    // slide 1's own picture of their book: the example stands over it until it is theirs, so back to the
    // original it is simply let go (panel-v1)
    if (el.classList.contains('ss-h1__theirs') && url === el.dataset.original) { el.dataset.swapped = ''; heroSync(); return; }
    setWebp(el, url);
    delete el.dataset.ssSrc;
    if (el.getAttribute('src') === url) return;
    el.classList.add('is-swapping');
    const done = () => { el.classList.remove('is-swapping'); el.removeEventListener('load', onLoad); el.removeEventListener('error', onError); };
    const onLoad = () => done();
    // A preview that does not arrive (the service mid-deploy, the bucket throttling, 7 Oct 2026):
    // the plain photograph comes back, never a broken picture or a cropped strip.
    const onError = () => { done(); if (el.dataset.original && url !== el.dataset.original) { el.dataset.swapped = ''; setWebp(el, el.dataset.original); el.src = el.dataset.original; } };
    el.addEventListener('load', onLoad);
    el.addEventListener('error', onError);
    el.src = url;
  }

  const showBusy = on => {
    if (busyEl) busyEl.hidden = !on;
    if (thumb) thumb.classList.toggle('is-rendering', !!on);
  };

  function showOriginal() {
    coverFor = '';
    views.forEach(el => { el.dataset.swapped = ''; setSrc(el, el.dataset.original); });
    if (f.preview) f.preview.value = '';
    last = null;
    arrange();                                               // the dedication page leaves with the name
  }

  /* (panel-v1: "Preview their edition", on once the first name is in, the one required field; the
     Library edition has its own Add to cart on the page. Before panel-v1:)
     The button: a STEP. Personalized: "Preview their book", on once the four required
     fields (First name, Surname, Dedication, Sign-off) have values — never waiting for
     Occasion, never waiting for a render — with a helper line while it is off, and off
     while the service says something does not fit. Library: "Add to bag". */
  const required = Array.from(root.querySelectorAll('[data-required]'));
  // The render on screen is the one for what is typed right now: the only state in
  // which a personalized line may be added, from the sheet or from the form.
  const renderCurrent = () => !!(last && last.key === JSON.stringify(spec()));
  // "Printed for: First Surname." — live, on every key, so the spelling is checked even without the
  // preview (Tom, 3 Oct). (panel-v1: the sign-off has its own row in the panel, shown exactly as it
  // prints; the page prints it as written, so no "From" is put in front of it here.)
  function printedLine() {
    if (!printed) return;
    const s = spec();
    if (s.copy_type === UNNAMED || !s.first) { printed.hidden = true; printed.textContent = ''; return; }
    printed.textContent = 'Printed for: ' + (s.first + ' ' + s.last).trim() + '.';
    printed.hidden = false;
  }
  function gate() {
    if (!submit) return;
    const s = spec();
    const library = s.copy_type === UNNAMED;
    const filled = required.every(el => el.value.trim() !== '');   // panel-v1: the first name is the one required field
    const blocked = !!(problems && problems.textContent);
    submit.disabled = library || !(filled && !blocked);
    // One gold button at a time (round 5): Preview until the review sheet has been opened for exactly what is typed,
    // then Add to cart; the other is the small link under it. Any change to the words brings Preview back.
    const seen = !library && filled && previewedFor === JSON.stringify(s);
    submit.dataset.role = seen ? 'link' : 'main';
    if (addLabel) addLabel.textContent = seen ? 'See their edition again' : 'Preview their edition';
    if (helper) helper.hidden = library || filled || blocked;
    // Add to cart at the panel's foot: live once there is a first name and their render for what is
    // typed has landed (the same condition as the sheet's own Add to cart, and as the form's second
    // button before panel-v1). The line under it says why it is waiting.
    if (addDirect) {
      addDirect.hidden = false;
      addDirect.disabled = library || !(filled && !blocked && renderCurrent());
      addDirect.dataset.role = seen ? 'main' : 'link';
      addDirect.textContent = (seen ? 'Add to cart · ' : 'or add to cart · ') + PRICE;
    }
    if (footnote && !footnote.classList.contains('is-bad')) {
      footnote.textContent = library ? '' : !s.first ? 'Add their first name to continue.'
        : blocked ? '' : !renderCurrent() ? (panelBookless() ? '' : 'Setting their cover and pages…') : 'Printed exactly as shown. Please check the spelling.';
    }
    root.classList.toggle('has-name', !library && !!s.first);
    printedLine();
    if (thumb) thumb.hidden = library || !s.first;
    const current = library || (s.first && JSON.stringify(s) === shownKey);
    if (!current && !library && s.first && !status.classList.contains('is-bad') && !panelBookless()) say('Preparing the preview…');
  }
  personal.forEach(el => el.addEventListener('input', printedLine));

  const STAGE_MS = 45000;
  const FAIL_MSG = 'Your preview is taking a moment, please try again.';
  async function request() {
    if (busy) { waiting = true; return; }                   // one at a time; the latest typing waits its turn
    const s = spec();
    const key = JSON.stringify(s);
    if (s.copy_type === UNNAMED || !s.first || key === shownKey) { gate(); return; }
    busy = true;
    say('Preparing the preview…');
    showBusy(true);
    let holdBusy = false;                                    // true while page slides are still loading
    try {
      // Two steps (Tom, 24 Sept): the cover first, so the photographs swap in while
      // the pages are being set; then the full render. The progress state stays
      // until the last page slide has loaded.
      // 45 seconds is the most any stage may take (Tom, 24 Sept): past that the
      // request is abandoned and the failure is said, in the sheet if it is open.
      // A failed request (no answer, or a 5xx) is tried once more after two seconds
      // before anything is said; an answer about the input comes straight back. A
      // stage that ran out its 45 seconds is not tried again.
      const ask = async stage => {
        for (let attempt = 0; ; attempt++) {
          const ctl = new AbortController();
          const cap = setTimeout(() => ctl.abort(), STAGE_MS);
          try {
            const r = await fetch(API + '/preview', {
              method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Storystir-Key': KEY },
              body: JSON.stringify(Object.assign({ stage }, s)), signal: ctl.signal,
            });
            const j = await r.json().catch(() => ({}));
            if (r.status < 500) return j;
            if (attempt) return {};
          } catch (err) {
            if (attempt || err.name === 'AbortError') throw err;
          } finally {
            clearTimeout(cap);
          }
          await new Promise(res => setTimeout(res, 2000));
        }
      };
      // the cover already made for exactly these words (the pages are asked for later): not asked for twice
      const cover = partial && partial.key === key ? { ok: true, urls: partial.urls } : await ask('cover');
      const fresh = () => JSON.stringify(spec()) === key;
      if (cover.ok && fresh()) {
        partial = { key, urls: cover.urls || {} };               // the cover is theirs now: the sheet shows it at once
        if (f.preview && cover.key && !f.preview.value) f.preview.value = cover.key;
        if (sheetIsOpen()) { sheetStage('pages'); sheetFill(); }
      }
      // the same name as the photographs on screen (only the words changed, 16 Oct 2026): their cover photographs
      // are already right and are left alone, never fetched again under another address
      const sameCover = coverFor === (s.first + ' ' + s.last).trim();
      if (cover.ok && fresh() && !sheetIsOpen() && !sameCover) {   // (with the sheet open, its own front loads first)
        coverFor = (s.first + ' ' + s.last).trim();          // the panel's cover is theirs once this front has loaded
        views.forEach(el => {                                // the photographs and the pinned cover, early
          const url = (cover.urls || {})[el.dataset.view];
          if (url && el.dataset.view.indexOf('shot_') === 0) { el.dataset.swapped = '1'; setSrc(el, url); }
        });
      }
      // The pages (12 Oct 2026): not for words that have changed since (the next render is
      // already waiting), and not before the four fields are in unless the sheet is open:
      // the server is one queue, and a page set nobody will see holds up the cover they will.
      // (panel-v1: with only the first name required, not while the name fields still have the focus either:
      // a last name typed next would otherwise wait behind a page set for the first name alone. Leaving
      // the name fields asks for the pages; the review sheet asks at once.)
      // A name left unchanged for 1.5 seconds gets its pages anyway (fullFor), the cover already in hand.
      const filledNow = required.every(el => el.value.trim() !== '');
      if (cover.ok && fresh() && filledNow && namesFocused() && !sheetIsOpen() && fullFor !== key) {
        clearTimeout(fullClock);
        fullClock = setTimeout(() => { if (JSON.stringify(spec()) === key && key !== shownKey) { fullFor = key; request(); } }, 1500);
        return;
      }
      if (cover.ok && (!fresh() || !(sheetIsOpen() || filledNow))) return;
      const j = cover.ok ? await ask('full') : cover;
      const now = spec();
      if (now.copy_type === UNNAMED || !now.first) {
        // they switched edition or cleared the name while this was rendering
      } else if (!j.ok) {
        const what = (j.problems || [FAIL_MSG]).join(' · ');
        if (sheetIsOpen()) sheetFail(what, false);
        else { if (problems) problems.textContent = what; say('', true); }
      } else {
        if (problems) problems.textContent = '';
        // One address per picture (12 Oct 2026): the cover pass may have answered from the service's
        // own copy while the bucket's was still uploading; the full pass names the bucket's. Keeping
        // the first address means a phone never downloads the same photograph twice.
        if (cover.urls) Object.keys(cover.urls).forEach(k => { if (k.indexOf('shot_') === 0 || k === 'front') j.urls[k] = cover.urls[k]; });
        let pending = 0;
        const landed = () => { if (--pending <= 0) showBusy(false); };
        const swapViews = () => {                            // shot_hero, shot_front, the pages, the panel's cover
          const keepCover = coverFor === (s.first + ' ' + s.last).trim();
          coverFor = (s.first + ' ' + s.last).trim();
          views.forEach(el => {
            const url = j.urls[el.dataset.view];
            if (!url) return;
            if (keepCover && el.dataset.view.indexOf('shot_') === 0 && el.dataset.swapped === '1' && el.getAttribute('src')) return;
            if (el.dataset.view.indexOf('open-') === 0 && el.closest('[data-slide]') && el.getAttribute('src') !== url) {
              pending++; el.loading = 'eager';                     // a lazy slide off screen would never report in
              el.addEventListener('load', landed, { once: true }); el.addEventListener('error', landed, { once: true });
            }
            el.dataset.swapped = '1'; setSrc(el, url);
          });
        };
        // With the sheet open, the page behind it waits: the sheet's own pictures have the connection.
        if (sheetIsOpen()) behind = swapViews; else swapViews();
        holdBusy = pending > 0;
        arrange();                                           // the dedication page joins the set
        if (f.preview) f.preview.value = j.key;
        shownKey = key;
        last = { key, urls: j.urls || {}, spec: s };
        partial = null;
        const stale = JSON.stringify(now) !== key;
        say(stale ? 'Preparing the preview…' : '');                  // the printed line above says what prints
        if (!stale) sheetFill();                             // a sheet already open on "Setting their pages…" fills now
      }
    } catch (e) {
      if (sheetIsOpen()) sheetFail(FAIL_MSG, true);
      else say(FAIL_MSG + ' Try again in a moment.', true);
    } finally {
      busy = false;
      if (!holdBusy) showBusy(false);
      gate();
      if (waiting) { waiting = false; request(); }
    }
  }

  function update() {
    const s = spec();
    const library = s.copy_type === UNNAMED;
    root.classList.toggle('is-library', library);
    if (edInput) edInput.value = ed;
    if (personalizeBtn) personalizeBtn.hidden = library;
    if (classicBox) classicBox.hidden = !library;
    if (classicLink) classicLink.hidden = library;
    if (unclassicLink) unclassicLink.hidden = !library;
    arrange();
    personal.forEach(el => { el.disabled = library; });        // a disabled field does not post with the cart
    if (library) {
      shownKey = '';
      showOriginal();
      if (problems) problems.textContent = '';
      say('');
    } else if (!s.first) {
      shownKey = '';
      showOriginal();
      say('');
    } else if (!(panelBookless() && !sheetIsOpen())) {
      request();                                             // First name alone is enough
    }
    gate();
  }

  const namesFocused = () => document.activeElement === f.first || document.activeElement === f.last;
  // leaving the name fields (to anything but the other name field) asks for the pages of what is typed
  [f.first, f.last].forEach(el => el && el.addEventListener('blur', () => setTimeout(() => {
    if (!namesFocused() && spec().first && JSON.stringify(spec()) !== shownKey) schedule(50);
  })));
  function schedule(ms) { clearTimeout(timer); if (problems) problems.textContent = ''; gate(); timer = setTimeout(update, ms); }
  // ~700ms after the last key (12 Oct 2026: was 600). Only ever one render in flight per visitor
  // (request() queues the latest); the service makes each cover once per name, so a change to the
  // dedication or the sign-off costs only the pages.
  // (16 Oct 2026) The dedication, the sign-off and the occasion only change the pages: they are set when the field
  // is folded (Done, Use ours, a tap outside) or after 1.5 seconds without a key, never per keystroke. The names
  // keep 700ms; their cover is set on the screen at once by the instant cover in any case.
  const WORDS_IDLE = 1500;
  personal.forEach(el => el.addEventListener('input', () => schedule(el === f.first || el === f.last ? 700 : WORDS_IDLE)));
  personal.forEach(el => el.addEventListener('focus', hello, { once: true }));
  // "Used personalizer" (Clarity smart event): the first thing typed into any field, once per page view
  let used = false;
  personal.forEach(el => el.addEventListener('input', e => { if (!used && e.isTrusted) { used = true; clarityEvent('Used personalizer'); } }));
  if (f.dedication && count) {
    const tick = () => { count.textContent = f.dedication.value.length + ' / ' + (f.dedication.maxLength || 320); };
    f.dedication.addEventListener('input', tick); tick();
  }
  /* ------------------------------------------------------------ the fields that print unless changed (panel-v1)
     The dedication and the sign-off open with a default (section settings) as a REAL value: left as it
     is, it posts in the same property as typed words and prints. Each is shown folded, what will print
     and "We'll print this unless you change it. Edit"; Edit unfolds the field. [first name] in the
     dedication's default follows the first name until the dedication itself is touched. The occasion
     starts blank and prints nothing until added. */
  const fillDefault = (tpl, first) => String(tpl || '').replace(/\[first name\]/gi, first || '').replace(/\s+([,.;!?])/g, '$1').replace(/\s{2,}/g, ' ').trim();
  const dedDefault = () => fillDefault(f.dedication && f.dedication.dataset.default, (f.first?.value || '').trim());
  let dedTouched = false;
  if (f.dedication) {
    f.dedication.value = dedDefault();
    f.dedication.addEventListener('input', e => { if (e.isTrusted) dedTouched = true; });
  }
  if (f.first && f.dedication) f.first.addEventListener('input', () => {
    if (dedTouched || !/\[first name\]/i.test(f.dedication.dataset.default || '')) return;
    f.dedication.value = dedDefault();
    preSync();
    if (count) count.textContent = f.dedication.value.length + ' / ' + (f.dedication.maxLength || 320);
  });
  const pres = Array.from(root.querySelectorAll('[data-pre]')).map(box => ({
    box, kind: box.dataset.pre,
    field: box.querySelector('input, textarea'),
    value: box.querySelector('[data-pre-value]'),
    note: box.querySelector('[data-pre-note]'),
    edit: box.querySelector('[data-pre-edit]'),
    word: box.querySelector('[data-pre-edit-word]'),
    editor: box.querySelector('[data-pre-editor]'),
  }));
  function preSync() {
    pres.forEach(p => {
      if (!p.field) return;
      const v = p.field.value.trim();
      const def = p.kind === 'dedication' ? dedDefault() : (p.field.dataset.default || '').trim();
      if (p.value) {
        p.value.textContent = v ? (p.kind === 'dedication' ? '\u201c' + v + '\u201d' : v) : (p.kind === 'occasion' ? 'None' : 'Nothing printed here');
        p.value.classList.toggle('is-empty', !v);
      }
      if (p.note) p.note.textContent = p.kind === 'occasion'
        ? (v ? 'Printed above the dedication' : 'Optional, printed above the dedication')
        : (v && v === def ? 'We\u2019ll print this unless you change it' : v ? 'Your words, printed as shown' : 'Left blank');
      if (p.word) p.word.textContent = v ? 'Edit' : 'Add';
    });
  }
  // quiet: folded by a tap or a move elsewhere, so the focus stays where they went (15 Oct 2026)
  function preOpen(p, open, quiet) {
    if (!open && !p.box.classList.contains('is-editing')) return;
    p.box.classList.toggle('is-editing', open);
    if (p.editor) p.editor.hidden = !open;
    if (p.edit) p.edit.setAttribute('aria-expanded', String(open));
    if (open && p.field) { p.field.focus(); try { const n = p.field.value.length; p.field.setSelectionRange(n, n); } catch (e) { /* not every field takes a selection */ } }
    else if (!open) {
      preSync(); if (!quiet && p.edit) p.edit.focus();
      if (spec().first && JSON.stringify(spec()) !== shownKey) schedule(50);     // folded: their pages for these words now
    }
  }
  pres.forEach(p => {
    if (p.edit) p.edit.addEventListener('click', () => preOpen(p, true));
    p.box.querySelectorAll('[data-pre-done]').forEach(b => b.addEventListener('click', () => preOpen(p, false)));
    // "Use ours" (16 Oct 2026): the book's own line (the sign-off's "With love") back in the field, which STAYS OPEN
    // with the caret at its end, to be read or tweaked (Tom's iPhone, round 4); only Done or a tap outside folds it.
    // "Write your own": the field emptied, open, the caret in it.
    p.box.querySelectorAll('[data-pre-reset]').forEach(b => b.addEventListener('click', () => {
      if (!p.field) return;
      if (p.kind === 'dedication') dedTouched = false;
      p.field.value = p.kind === 'dedication' ? dedDefault() : (p.field.dataset.default || '');
      p.field.dispatchEvent(new Event('input', { bubbles: true }));
      p.field.focus();
      try { const n = p.field.value.length; p.field.setSelectionRange(n, n); } catch (e) { /* not every field takes a selection */ }
    }));
    p.box.querySelectorAll('[data-pre-clear]').forEach(b => b.addEventListener('click', () => {
      if (!p.field) return;
      if (p.kind === 'dedication') dedTouched = true;
      p.field.value = '';
      p.field.dispatchEvent(new Event('input', { bubbles: true }));
      p.field.focus();
    }));
    if (p.field) {
      p.field.addEventListener('input', preSync);
      // Enter in a one-line field folds it again rather than doing nothing
      if (p.field.tagName === 'INPUT') p.field.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); preOpen(p, false); } });
    }
  });
  /* A clear way out of an open field (15 Oct 2026, Tom's iPhone): the Done button under it, or a tap anywhere
     outside it, folds it again with what was typed kept (every key is already in the field's value). A tap, not a
     touch: a scroll of the panel leaves it open. Focus moving to another control folds it too; a tap on nothing
     focusable (iOS gives the focus to no one) is left to the click. */
  // (16 Oct 2026, Tom's iPhone: "Use ours" and "Write your own" only folded the field.) On a touch, WebKit blurs
  // the field at mousedown and hands the focus to <main>; that read as "moved outside", the field folded, and the
  // tap's click then landed on whatever had moved under the finger. Now the editor's own buttons never take the
  // focus from the field (their mousedown is kept from it), a press that starts inside the editor
  // never folds it, and only a move to another control outside it does; a tap elsewhere is left to the click.
  const CONTROL = 'input, textarea, select, button, a[href], [contenteditable="true"]';
  // the press now under way started inside this editor (set by every press anywhere; a key clears it)
  pres.forEach(p => { p.downIn = false; });
  const pressAt = e => { pres.forEach(p => { p.downIn = p.box.contains(e.target); }); };
  document.addEventListener('pointerdown', pressAt, true);
  document.addEventListener('touchstart', pressAt, { capture: true, passive: true });
  document.addEventListener('keydown', () => pres.forEach(p => { p.downIn = false; }), true);
  // mousedown only: a pointerdown kept from WebKit after a touch cancels the click as well
  pres.forEach(p => p.box.querySelectorAll('[data-pre-reset], [data-pre-clear], [data-pre-done]').forEach(b => b.addEventListener('mousedown', e => e.preventDefault())));
  document.addEventListener('click', e => {
    pres.forEach(p => { if (p.box.classList.contains('is-editing') && !p.box.contains(e.target) && !e.target.closest('[data-pre-edit]') && !p.downIn) preOpen(p, false, true); });
  });
  pres.forEach(p => p.box.addEventListener('focusout', e => {
    const to = e.relatedTarget;
    if (to && !p.box.contains(to) && to.matches && to.matches(CONTROL) && !p.downIn) preOpen(p, false, true);
  }));
  if (f.dedication && count) count.textContent = f.dedication.value.length + ' / ' + (f.dedication.maxLength || 320);
  preSync();

  /* ------------------------------------------------------------ the review sheet */
  /* The sheet owns its own state (Tom, 24 Sept). Its gallery is the page's own slides
     with the one set of controls (assets/ss-gallery.js); "Setting their pages…" sits over
     the frame until the render lands, and the button reads the same until every page
     slide has loaded; anything that fails while the sheet is open is said IN the sheet,
     under the gallery, with Retry and Edit; nothing runs past 45 seconds. There is no
     checkbox: the click on Add to cart is the confirmation, recorded as _proof_confirmed. */
  const sheet = root.querySelector('[data-sheet]');
  const sheetTrack = root.querySelector('[data-sheet-track]');
  const sheetNav = root.querySelector('[data-sheet] [data-gnav]');
  const sheetCaption = root.querySelector('[data-sheet-caption]');
  const sheetWait = root.querySelector('[data-sheet-wait]');
  const sheetError = root.querySelector('[data-sheet-error]');
  const sheetErrorText = root.querySelector('[data-sheet-error-text]');
  const sheetRetry = root.querySelector('[data-sheet-retry]');
  const sheetNote = root.querySelector('[data-sheet-note]');
  const sheetAdd = root.querySelector('[data-sheet-add]');
  // The same slides as the page's gallery and the home demo (Tom, 24 Sept): front, the
  // book, back, then their four pages. 3D only: never a flat print render, no wide
  // shot. The back is the title's own photograph: nothing on it is theirs.
  // panel-v1 ("Preview their edition" shows the cover and the dedication page): the cover, the dedication
  // page, then the book standing and the other pages with their name. The back photograph left the set.
  const SLIDES = [['shot_front', 'Their cover'], ['open-dedication', 'Dedication page'], ['shot_hero', 'The book'],
                  ['open-title', 'Title page'], ['open-bookplate', 'Ownership page'], ['open-colophon', 'Closing page']];
  const esc = v => String(v).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  let lastFocus = null, sheetGal = null, sheetReady = false, sheetLoading = false, sheetClock = 0;

  const sheetIsOpen = () => !!(sheet && !sheet.hidden);
  // (12 Oct 2026, the Clarity report: one visitor sat through two blank "Setting their pages" sheets
  // and left.) The button always reads "Add to cart · $95"; it works from the moment their cover,
  // with their name on it, is on the screen. The click is still the confirmation of what they see.
  function sheetButton() {
    if (!sheetAdd) return;
    sheetAdd.disabled = !sheetReady;
    sheetAdd.textContent = 'Add to cart · ' + PRICE;
  }
  // Progress in words, with their first name: the cover, then the dedication, then the last pages.
  const sheetProg = root.querySelector('[data-sheet-progress-line]');
  const sheetProgText = root.querySelector('[data-sheet-progress-text]');
  const sheetWaitText = root.querySelector('[data-sheet-wait-text]');
  const sheetBar = root.querySelector('[data-sheet-bar]');
  function sheetStage(stage, overStandIn) {
    const first = (spec().first || '').trim();
    const words = {
      cover: first ? 'Printing ' + first + '\u2019s name on the cover\u2026' : 'Printing their name on the cover\u2026',
      pages: 'Setting the dedication\u2026',
      finish: 'Almost ready\u2026',
    }[stage];
    const share = { cover: 0.2, pages: 0.55, finish: 0.85, done: 1 }[stage] || 0;
    if (sheetWaitText && words) sheetWaitText.textContent = words;
    if (sheetProgText && words) sheetProgText.textContent = words;
    if (sheetBar) { sheetBar.style.transform = 'scaleX(' + share + ')'; sheetBar.classList.toggle('is-done', stage === 'done'); }
    if (sheetProg) sheetProg.hidden = (stage === 'cover' && !overStandIn) || stage === 'done';
  }
  function sheetWaiting(on) {
    if (on) sheetStage('cover');
    if (sheetWait) sheetWait.hidden = !on;
    if (on && sheetError) sheetError.hidden = true;
    if (sheetNav) sheetNav.hidden = on || !sheetGal || sheetGal.slides().length < 2;
    sheetLoading = on; sheetButton();
    clearTimeout(sheetClock);
    if (on) sheetClock = setTimeout(() => sheetFail(FAIL_MSG, true), STAGE_MS);   // the spinner can never run on
  }
  function sheetFail(msg, retryable) {
    if (!sheetIsOpen()) return;
    clearTimeout(sheetClock);
    if (sheetWait) sheetWait.hidden = true;
    if (sheetErrorText) sheetErrorText.textContent = msg;
    if (sheetRetry) sheetRetry.hidden = !retryable;
    if (sheetError) sheetError.hidden = false;
    if (sheetNav) sheetNav.hidden = true;                    // the message stands over the frame
    if (sheetProg) sheetProg.hidden = true;
    sheetReady = false; sheetLoading = false; sheetButton();
  }
  // Page slides wait in place, each with its own line, and fill in one by one as their pictures land.
  const PAGE_WORDS = { 'open-title': 'Setting the title page\u2026', 'open-dedication': 'Setting the dedication\u2026',
                       'open-bookplate': 'Setting the ownership page\u2026', 'open-colophon': 'Setting the closing page\u2026' };
  // Never rebuild the slides while they are moving (Tom's iPhone, 12 Oct 2026): a refill in the middle of
  // a slide change left the track stopped between two slides, a dark band down one side. Wait until it
  // rests on a slide, then fill.
  let fillWait = 0;
  const sheetMoving = () => sheetTrack.children.length > 1 && sheetTrack.clientWidth > 0 &&
    Math.abs(sheetTrack.scrollLeft / sheetTrack.clientWidth - Math.round(sheetTrack.scrollLeft / sheetTrack.clientWidth)) > 0.01;
  function sheetFill() {
    if (!sheetIsOpen()) return;
    clearTimeout(fillWait);
    if (sheetMoving()) { fillWait = setTimeout(sheetFill, 120); return; }
    const s = spec(), key = JSON.stringify(s);
    const ready = last && last.key === key;
    const early = !ready && partial && partial.key === key;  // the cover is in, the pages are still being set
    if (!ready && !early) {
      if (instant && s.first) { sheetInstant(s); return; }  // their name on the cover at once
      sheetWaiting(true); return;                            // nothing of theirs yet: the words say what is happening
    }
    clearTimeout(sheetClock);
    // the back photograph is the title's own (nothing on it is theirs); the render's
    // own "back" is the flat board print and must never stand in for it
    const urls = Object.assign({}, ready ? last.urls : partial.urls, { back: root.dataset.back || '' });
    // a phone takes the pages' phone copies (half the bytes; the desktop zoom keeps the large ones)
    if (!window.matchMedia('(min-width: 1024px)').matches) {
      Object.keys(urls).forEach(k => { if (k.indexOf('open-') === 0 && urls['m-' + k]) urls[k] = urls['m-' + k]; });
    }
    const keep = sheetGal && sheetTrack.children.length ? sheetGal.index() : 0;   // where they are when the pages arrive
    // Their real front, once loaded, stays the very same element through the refill (no reload, no
    // flicker); the stand-in stays under a real front that is still loading, and fades out under it.
    const sameKey = sheetTrack.dataset.key === key;
    const oldFront = sameKey ? sheetTrack.querySelector('[data-sheet-slide="shot_front"]') : null;
    const oldImg = oldFront && oldFront.querySelector('img');
    const keptFront = oldImg && (oldImg.classList.contains('is-in') || oldImg.getAttribute('src') === urls.shot_front) ? oldFront : null;   // loaded, or still loading the same picture
    const stand = keptFront ? null : sheetTrack.querySelector('[data-instant]');
    if (stand) stand.remove();
    if (keptFront) keptFront.remove();
    const slides = SLIDES.filter(([k]) => urls[k] || (early && PAGE_WORDS[k] && (k !== 'open-dedication' || (s.dedication || '').trim())));
    sheetTrack.innerHTML = slides.map(([k, cap]) =>
      '<figure class="ss-sheet__slide" data-sheet-slide="' + k + '" data-caption="' + cap + '">' +
      (PAGE_WORDS[k] ? '<div class="ss-sheet__pagewait" data-sheet-progress><span class="ss-gallery__ring" aria-hidden="true"></span><span>' + PAGE_WORDS[k] + '</span></div>' : '') +
      (urls[k] ? '<img ' + (k === 'shot_front' ? 'src' : 'data-later') + '="' + esc(urls[k]) + '" alt="' + cap + '"' +
        (PAGE_WORDS[k] || k === 'shot_front' ? ' class="is-loading"' : '') + (k === 'shot_front' ? ' fetchpriority="high"' : '') + '>' : '') +
      '</figure>').join('');
    sheetTrack.dataset.key = key;
    if (keptFront) {
      const slot = sheetTrack.querySelector('[data-sheet-slide="shot_front"]');
      if (slot) slot.replaceWith(keptFront);
    }
    if (!keep) sheetTrack.scrollLeft = 0;
    // Add to cart works once their real front is on the screen (the click confirms what they see)
    const frontImg = sheetTrack.querySelector('[data-sheet-slide="shot_front"] img');
    // The front has the connection to itself (a phone on 4G shares about 1.6 Mb/s between every
    // picture asked for); the rest are asked for once it is on screen, and the page behind last.
    const later = () => {
      const ask = im => { if (im.dataset.later) { im.src = im.dataset.later; im.removeAttribute('data-later'); } };
      const rest = Array.from(sheetTrack.querySelectorAll('img[data-later]'));
      const pagesFirst = rest.filter(im => im.closest('[data-sheet-slide^="open-"]'));
      pagesFirst.forEach(ask);                               // their pages next; the photographs after them
      const others = rest.filter(im => pagesFirst.indexOf(im) < 0);
      let left = pagesFirst.length;
      const then = () => { if (--left <= 0) others.forEach(ask); };
      if (!left) others.forEach(ask);
      pagesFirst.forEach(im => { im.addEventListener('load', then, { once: true }); im.addEventListener('error', then, { once: true }); });
      setTimeout(() => others.forEach(ask), 8000);           // never held for long
    };
    const frontIn = () => {
      later();
      frontImg.classList.remove('is-loading'); frontImg.classList.add('is-in');
      const st = frontImg.parentNode.querySelector('[data-instant]');
      if (st) setTimeout(() => st.remove(), 400);            // after the fade
      if (sheetTrack.dataset.key === JSON.stringify(spec())) { sheetReady = true; sheetButton(); }
    };
    sheetReady = false; sheetLoading = !ready; sheetButton();
    if (!frontImg) { later(); sheetReady = true; sheetButton(); }   // a title with no named front photograph: the boards stand
    else if (frontImg.classList.contains('is-in')) frontIn();
    else {
      if (stand) { frontImg.parentNode.insertBefore(stand, frontImg); instantName(stand, (s.first + ' ' + s.last).trim()); }
      if (frontImg.complete && frontImg.naturalWidth > 0) frontIn();
      else {
        frontImg.addEventListener('load', frontIn, { once: true });
        frontImg.addEventListener('error', () => {           // the cover will not come: say so in the sheet
          if (sheetIsOpen() && sheetTrack.dataset.key === JSON.stringify(spec())) sheetFail(FAIL_MSG, true);
        }, { once: true });
      }
    }
    const pages = Array.from(sheetTrack.querySelectorAll('[data-sheet-slide^="open-"] img'));
    let pending = ready ? pages.length : -1;
    sheetStage(ready ? (pending ? 'finish' : 'done') : 'pages');
    if (ready && !pending) releaseBehind();
    const landed = img => {
      img.classList.remove('is-loading');
      const w = img.parentNode.querySelector('.ss-sheet__pagewait'); if (w) w.remove();
      if (--pending === 0 && sheetIsOpen()) { sheetLoading = false; sheetStage('done'); releaseBehind(); }
    };
    pages.forEach(img => {
      if (!img.dataset.later && img.complete && img.naturalWidth > 0) { landed(img); return; }
      img.addEventListener('load', () => landed(img), { once: true });
      img.addEventListener('error', () => {                // one page that will not come: say so on that page, keep the rest
        const w = img.parentNode.querySelector('.ss-sheet__pagewait');
        if (w) w.lastChild.textContent = 'This page is taking a moment.';
        if (--pending === 0) { sheetStage('done'); releaseBehind(); }
      }, { once: true });
    });
    if (sheetWait) sheetWait.hidden = true;
    if (sheetError) sheetError.hidden = true;
    if (!ready) sheetClock = setTimeout(() => { if (!(last && last.key === JSON.stringify(spec()))) sheetFail(FAIL_MSG, true); }, STAGE_MS);
    sheetGalleryUp(keep);
  }
  // The stand-in (12 Oct 2026): the blank front with their name set on it, and their pages waiting
  // in place. Add to cart stays off; the words say what is happening.
  function sheetInstant(s) {
    clearTimeout(sheetClock);
    let stand = sheetTrack.dataset.key === 'instant' ? sheetTrack.querySelector('[data-instant]') : null;
    if (!stand) {
      const pages = SLIDES.filter(([k]) => PAGE_WORDS[k] && (k !== 'open-dedication' || (s.dedication || '').trim()));
      sheetTrack.innerHTML = '<figure class="ss-sheet__slide" data-sheet-slide="shot_front" data-caption="Front"></figure>' +
        pages.map(([k, cap]) => '<figure class="ss-sheet__slide" data-sheet-slide="' + k + '" data-caption="' + cap + '">' +
          '<div class="ss-sheet__pagewait" data-sheet-progress><span class="ss-gallery__ring" aria-hidden="true"></span><span>' +
          PAGE_WORDS[k] + '</span></div></figure>').join('');
      stand = instantCover();
      sheetTrack.firstChild.appendChild(stand);
      sheetTrack.dataset.key = 'instant';
      sheetTrack.scrollLeft = 0;
    }
    instantName(stand, (s.first + ' ' + s.last).trim());
    if (sheetWait) sheetWait.hidden = true;
    if (sheetError) sheetError.hidden = true;
    sheetReady = false; sheetLoading = true; sheetButton();
    sheetStage('cover', true);
    sheetClock = setTimeout(() => sheetFail(FAIL_MSG, true), STAGE_MS);   // the stand-in can never stand for ever
    sheetGalleryUp(0);
  }
  // the page behind the sheet takes its new pictures once the sheet's own have all landed
  function releaseBehind() { if (behind) { const go = behind; behind = null; setTimeout(go, 300); } }
  function sheetGalleryUp(keep) {
    zoomClose();                                             // a new set of slides opens fitted
    if (sheetGal) { sheetGal.rebuild(); if (keep) sheetGal.go(keep, true); } else if (window.ssGallery && sheetNav) {
      sheetGal = window.ssGallery(sheetTrack, sheetNav, {
        slide: '[data-sheet-slide]',
        label: slide => 'Show ' + (slide.dataset.caption || '').toLowerCase(),
        onMark: (i, slide) => {
          if (sheetCaption) sheetCaption.textContent = slide ? slide.dataset.caption : '';
          if (zoomed() && slide) zoomShow(slide);            // the zoom follows the arrows and dots
        },
      });
    }
    if (sheetNav) sheetNav.hidden = !sheetGal || sheetGal.slides().length < 2;
  }

  /* ------------------------------------------------------------ desktop zoom (3 Oct)
     One click on the slide: the render at its native size (the source is 1024 x 1536; the
     page slides larger), centred in the frame; drag or scroll pans; a click or Escape fits it
     again. Desktop only (1024px and up): a phone keeps the fitted slide and a swipe. */
  const zoom = root.querySelector('[data-sheet-zoom]');
  const zoomImg = root.querySelector('[data-sheet-zoom-img]');
  const desktop = () => window.matchMedia('(min-width: 1024px)').matches;
  const zoomed = () => !!(zoom && !zoom.hidden);
  function zoomShow(slide) {
    const img = slide && slide.querySelector('img');
    if (!zoom || !zoomImg || !img) return;
    const place = () => {
      zoomImg.style.width = (zoomImg.naturalWidth || 1024) + 'px';
      zoomImg.style.height = (zoomImg.naturalHeight || 1536) + 'px';
      // centred on the page area: the middle of the image under the middle of the frame
      zoom.scrollLeft = Math.max(0, (zoom.scrollWidth - zoom.clientWidth) / 2);
      zoom.scrollTop = Math.max(0, (zoom.scrollHeight - zoom.clientHeight) / 2);
    };
    if (zoomImg.getAttribute('src') !== img.getAttribute('src')) {
      zoomImg.addEventListener('load', place, { once: true });
      zoomImg.src = img.getAttribute('src');
      zoomImg.alt = img.alt || '';
    } else place();
    zoom.hidden = false;
    sheet.classList.add('is-zoomed');
    if (zoomImg.complete && zoomImg.naturalWidth) place();
  }
  function zoomClose() {
    if (!zoom || zoom.hidden) return;
    zoom.hidden = true;
    sheet.classList.remove('is-zoomed');
    if (sheetTrack) sheetTrack.focus({ preventScroll: true });
  }
  if (zoom && sheetTrack) {
    sheetTrack.addEventListener('click', e => {
      if (!desktop() || !sheetReady) return;
      const slide = e.target.closest('[data-sheet-slide]');
      if (!slide || !e.target.closest('img')) return;
      zoomShow(slide);
      zoom.focus({ preventScroll: true });
    });
    // drag to pan; a click that did not drag fits the slide again
    let down = null, moved = false;
    zoom.addEventListener('mousedown', e => { if (e.button !== 0) return; down = { x: e.clientX, y: e.clientY, l: zoom.scrollLeft, t: zoom.scrollTop }; moved = false; zoom.classList.add('is-dragging'); e.preventDefault(); });
    window.addEventListener('mousemove', e => {
      if (!down) return;
      const dx = e.clientX - down.x, dy = e.clientY - down.y;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) moved = true;
      zoom.scrollLeft = down.l - dx; zoom.scrollTop = down.t - dy;
    });
    window.addEventListener('mouseup', () => { if (!down) return; const wasDrag = moved; down = null; zoom.classList.remove('is-dragging'); if (!wasDrag) zoomClose(); });
    window.addEventListener('resize', () => { if (zoomed() && !desktop()) zoomClose(); });
  }
  /* "Book previewed" for the ad pixels (8 Oct): the review sheet opening is the step between
     personalizing and the bag. Published as a Shopify custom event; the "Storystir: book previewed"
     custom pixel (Settings > Customer events) sends it to Meta as CustomizeProduct, under the
     visitor's consent. The product only: never the name, the dedication or the sign-off. */
  function previewed() {
    try {
      const a = window.Shopify && window.Shopify.analytics;
      const p = window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product;
      if (!a || typeof a.publish !== 'function' || !p) return;
      const v = (p.variants && p.variants[0]) || {};
      a.publish('storystir_book_previewed', {
        product_id: String(p.id), variant_id: v.id ? String(v.id) : '', title: v.name || '',
        price: typeof v.price === 'number' ? v.price / 100 : null, currency: (window.Shopify.currency && window.Shopify.currency.active) || 'USD'
      });
    } catch (e) { /* tracking never gets in the way of the preview */ }
  }
  let previewedFor = '';                                     // the words their edition was last seen with (round 5)
  function sheetOpen() {
    if (!sheet) return;
    previewedFor = JSON.stringify(spec());
    lastFocus = document.activeElement;
    sheetReady = false; sheetLoading = true; sheetButton();
    sheetTrack.innerHTML = ''; delete sheetTrack.dataset.key; if (sheetCaption) sheetCaption.textContent = '';
    if (sheetError) sheetError.hidden = true;
    if (sheetNav) sheetNav.hidden = true;
    sheet.hidden = false;
    lock('sheet');
    previewed();
    clarityEvent('Preview viewed');
    sheetFill();
    if (last === null || last.key !== JSON.stringify(spec())) { clearTimeout(timer); request(); }   // nothing current: make it now; the sheet says "Setting their pages…"
    (sheet.querySelector('[data-sheet-close]') || sheet).focus();
  }
  function sheetClose() {
    if (!sheet || sheet.hidden) return;
    if (behind) { const go = behind; behind = null; go(); }
    zoomClose();
    clearTimeout(sheetClock);
    if (sheetError && !sheetError.hidden) say('');           // the failure was said in the sheet; the form does not repeat it
    sheet.hidden = true;
    unlock('sheet');
    if (lastFocus && lastFocus.focus) lastFocus.focus();
    panelCover();
    gate();                                                   // the foot now offers Add to cart (round 5)
  }
  function sheetRetryNow() {
    if (sheetError) sheetError.hidden = true;
    shownKey = ''; last = null; partial = null;
    sheetWaiting(true);
    clearTimeout(timer); request();
  }
  if (sheet) {
    sheet.querySelectorAll('[data-sheet-close]').forEach(b => b.addEventListener('click', sheetClose));
    sheet.addEventListener('click', e => { if (e.target === sheet) sheetClose(); });
    if (sheetRetry) sheetRetry.addEventListener('click', sheetRetryNow);
    if (sheetAdd) sheetAdd.addEventListener('click', () => addToBag(true));
  }
  if (thumb) thumb.addEventListener('click', sheetOpen);

  /* ------------------------------------------------------------ the bag */
  const bag = root.querySelector('[data-bag]');
  const bagThumb = root.querySelector('[data-bag-thumb]');
  const bagMeta = root.querySelector('[data-bag-meta]');
  const bagCount = root.querySelector('[data-bag-count]');

  function properties(confirmed) {
    const s = spec();
    const p = { 'Edition': edition(), '_slug': slug };
    if (s.copy_type !== UNNAMED) {
      Object.assign(p, { 'Recipient name': s.first, 'Surname': s.last, 'Dedication': s.dedication,
                         'Sign-off': s.signed_off, 'Occasion': s.occasion, '_preview': f.preview ? f.preview.value : '' });
      if (confirmed) { p['_proof_confirmed'] = 'yes'; p['_proof_confirmed_at'] = new Date().toISOString(); }
    }
    return p;
  }

  async function addToBag(confirmed, direct) {
    const s = spec();
    const library = s.copy_type === UNNAMED;
    // A personalized line needs their render on screen for what is typed now: from the sheet
    // once its pages are in, or from the form's own Add to cart (3 Oct) under the same rule.
    // Either click is the confirmation; the properties are the same from both.
    if (footnote) footnote.classList.remove('is-bad');
    if (buyStatus) { buyStatus.textContent = ''; buyStatus.classList.remove('is-bad'); }
    if (!library && !(confirmed && (direct ? renderCurrent() : sheetReady))) {
      if (direct) say(required.every(el => el.value.trim() !== '') ? 'Their pages are still being set. One moment.' : 'Add their name and your message first.', true);
      return;
    }
    const id = form ? form.querySelector('[name="id"]').value : '';
    const btn = direct ? addDirect : (library ? classicAdd : sheetAdd);
    if (btn) btn.disabled = true;
    try {
      const r = await fetch(root.dataset.cartAdd || '/cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1, properties: properties(confirmed) }] }),
      });
      if (!r.ok) throw new Error('add failed ' + r.status);
      clarityEvent('Add to cart', ['edition', library ? 'library' : 'personalized']);
      sheetClose();
      panelClose(true);
      // the line just added: their cover for a personalized book, the product's front photograph for the Library edition
      if (bagThumb) bagThumb.src = (!library && last && last.urls.shot_front) || root.dataset.front || bagThumb.src;
      if (bagMeta) bagMeta.textContent = library ? 'Library edition' : 'Personalized for ' + (s.first + ' ' + s.last).trim();
      bagOpen();
      try {
        const c = await (await fetch((root.dataset.cart || '/cart') + '.js', { headers: { 'Accept': 'application/json' } })).json();
        const n = c.item_count;
        if (bagCount) bagCount.textContent = n + (n === 1 ? ' item in your bag' : ' items in your bag');
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble) { bubble.setAttribute('aria-label', 'Cart, ' + n + (n === 1 ? ' item' : ' items')); bubble.dataset.count = n; }
      } catch (e) { /* the count is a courtesy */ }
    } catch (e) {
      if (sheetIsOpen()) sheetFail('The book could not be added just now.', false);
      else if (library) { if (buyStatus) { buyStatus.textContent = 'The book could not be added just now. Try again in a moment.'; buyStatus.classList.add('is-bad'); } }
      else if (footnote) { footnote.textContent = 'The book could not be added just now. Try again in a moment.'; footnote.classList.add('is-bad'); }
      else say('The book could not be added just now. Try again in a moment.', true);
    } finally {
      if (btn) btn.disabled = direct ? !renderCurrent() : (library ? false : !sheetReady);
    }
  }
  if (addDirect) addDirect.addEventListener('click', () => addToBag(true, true));
  function bagOpen() {
    if (!bag) return;
    bag.hidden = false;
    lock('bag');
    (bag.querySelector('[data-bag-checkout]') || bag).focus();
  }
  function bagClose() {
    if (!bag || bag.hidden) return;
    bag.hidden = true;
    unlock('bag');
  }
  if (bag) {
    bag.querySelectorAll('[data-bag-close]').forEach(b => b.addEventListener('click', bagClose));
    bag.addEventListener('click', e => { if (e.target === bag) bagClose(); });
  }
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') {
      if (zoomed()) { zoomClose(); return; }
      if (bag && !bag.hidden) { bagClose(); return; }
      if (sheetIsOpen()) { sheetClose(); return; }
      if (panelIsOpen()) panelClose();
      return;
    }
    if (e.key === 'Tab') trapTab(e);
    // the arrows keep working while zoomed, wherever the focus is inside the sheet
    if (zoomed() && (e.key === 'ArrowRight' || e.key === 'ArrowLeft') && sheetGal) { e.preventDefault(); sheetGal.go(sheetGal.index() + (e.key === 'ArrowRight' ? 1 : -1)); }
  });

  // "Preview their edition" (in the panel) opens the review sheet; the Library edition's button adds straight to the bag
  if (submit) submit.addEventListener('click', () => { if (spec().copy_type !== UNNAMED) sheetOpen(); });
  if (classicAdd) classicAdd.addEventListener('click', () => addToBag(false));

  /* ------------------------------------------------------------ the page under a layer (panel-v1)
     One lock for the panel, the review sheet and the bag. The body is fixed in place rather than only
     overflow: hidden, which iOS Safari and the Facebook and Instagram in-app browsers ignore; the
     scroll position comes back exactly when the last layer closes. */
  const locks = new Set();
  let lockY = 0;
  function lock(k) {
    if (!locks.size) {
      lockY = window.scrollY || window.pageYOffset || 0;
      document.body.style.top = (-lockY) + 'px';
      document.body.classList.add('ss-modal-open', 'ss-locked');
    }
    locks.add(k);
    stickySync();
  }
  function unlock(k) {
    if (!locks.delete(k) || locks.size) { stickySync(); return; }
    document.body.classList.remove('ss-modal-open', 'ss-locked');
    document.body.style.top = '';
    window.scrollTo(0, lockY);
    stickySync();
  }

  /* ------------------------------------------------------------ slide 1: the book standing (panel-v1)
     The example names are laid into the blank name line of the photograph (snippets/ss-callout-geometry,
     the phone hero's "patch"), and change every few seconds, the way the home hero's do; one name, still,
     with reduced motion. The gold pill sits on the cloth under the label. Both are placed for the slide's
     crop (object-fit: cover), on every resize. Once their own render of the standing book has loaded,
     it takes the slide over (heroSync); back to the example when the name is cleared. */
  function heroSync() {
    const h = root.querySelector('[data-h1]');
    const t = h && h.querySelector('.ss-h1__theirs');
    if (!t) return;
    h.classList.toggle('is-theirs', t.dataset.swapped === '1' && !!t.getAttribute('src') && t.complete && t.naturalWidth > 0);
  }
  (function hero() {
    const h1 = root.querySelector('[data-h1]');
    if (!h1) return;
    // the ONE pill over the gallery frame (round 6: not inside the slide any more), placed from slide 1's geometry
    const gal = h1.closest('[data-gallery]');
    const pill = (gal || h1).querySelector('[data-h1-pill]');
    const phoneMQ = window.matchMedia('(max-width: 1023px)');
    const box = h1.querySelector('[data-h1-names]');
    const theirs = h1.querySelector('.ss-h1__theirs');
    if (theirs) { theirs.addEventListener('load', heroSync); theirs.addEventListener('error', () => { theirs.dataset.swapped = ''; heroSync(); }); }
    let g = null;
    try { const el = h1.querySelector('[data-h1-geo]'); g = el ? (JSON.parse(el.textContent)[slug] || {}).phone || null : null; } catch (e) { g = null; }
    const W0 = (g && g.size && g.size[0]) || 1024, H0 = (g && g.size && g.size[1]) || 1536;
    // under the label's foot, centred on it: the fixed hero quad every title is shot into (tools/hero_fixed_quad.py)
    const L = (g && g.label) || [[389.4, 406.5], [727.7, 360.4], [721.4, 1041.0], [387.9, 1119.8]];
    const anchor = [(L[2][0] + L[3][0]) / 2, Math.max(L[2][1], L[3][1]) + 84];
    function layout() {
      const W = h1.clientWidth, H = h1.clientHeight;
      if (!W || !H) return;
      const k = Math.max(W / W0, H / H0), x = (W - W0 * k) / 2, y = (H - H0 * k) / 2;
      if (box && g && g.patch) {
        const p = g.patch;
        Object.assign(box.style, { left: (x + p[0] * k) + 'px', top: (y + p[1] * k) + 'px', width: (p[2] * k) + 'px', height: (p[3] * k) + 'px' });
        box.classList.add('is-placed');
      }
      if (pill && !phoneMQ.matches) {                           // desktop: the stylesheet's place, as before
        pill.style.top = pill.style.bottom = ''; if (gal) gal.classList.remove('is-pill-nav');
      } else if (pill && g) {
        // (17 Oct 2026) phones: below the label panel, never over the name, clear of the arrows and dots along the
        // foot of the frame; where a short frame leaves no room between them, in the arrows' row (the dots and the
        // count give way there), kept on screen. In the pill's own frame's pixels (it is not inside the slide).
        const ph = pill.offsetHeight || 36, hr = h1.getBoundingClientRect(), fr = (pill.offsetParent || h1).getBoundingClientRect(), dy = hr.top - fr.top;
        const navs = gal ? Array.from(gal.querySelectorAll('.ss-gnav__arrow, [data-gnav-dots], [data-gnav-count]')).map(e => e.getBoundingClientRect()).filter(r => r.width && r.height) : [];
        const navTop = navs.length ? Math.min(...navs.map(r => r.top)) - fr.top - 4 : H + dy;
        const nameBot = (g.patch ? y + (g.patch[1] + g.patch[3]) * k : y + Math.max(L[2][1], L[3][1]) * k) + dy;
        let top = y + Math.max(L[2][1], L[3][1]) * k + 8 + dy, inNav = false;
        if (top + ph > navTop) top = navTop - ph;
        if (top < nameBot + 4) {
          const ar = gal && gal.querySelector('.ss-gnav__arrow'), r = ar && ar.getBoundingClientRect();
          inNav = !!(r && r.height);
          const screenBot = window.innerHeight - (fr.top + window.scrollY) - 4;   // the first screen (page top), whatever the scroll
          top = Math.max(nameBot + 4, Math.min(inNav ? (r.top + r.bottom) / 2 - fr.top - ph / 2 : nameBot + 4, screenBot - ph));
        }
        if (gal) gal.classList.toggle('is-pill-nav', inNav);
        Object.assign(pill.style, { top: top.toFixed(1) + 'px', bottom: 'auto' });
      }
      if (pill) pill.classList.add('is-placed');
    }
    layout();
    if ('ResizeObserver' in window) new ResizeObserver(layout).observe(h1); else window.addEventListener('resize', layout);
    phoneMQ.addEventListener && phoneMQ.addEventListener('change', layout);
    // the names run once the page has loaded, only while the slide is on screen and not yet theirs
    let urls = [];
    try { const el = h1.querySelector('[data-h1-name-urls]'); urls = el ? JSON.parse(el.textContent) : []; } catch (e) { urls = []; }
    if (!box || calm || urls.length < 2) return;
    let imgs = null, i = 0, clock = 0, seen = true;
    const step = () => {
      if (!seen || document.hidden || h1.classList.contains('is-theirs')) return;
      const next = (i + 1) % imgs.length, a = imgs[i], b = imgs[next];
      if (!b.complete || !b.naturalWidth) return;               // not loaded yet: this name stays a little longer
      if (!b.dataset.ssDecoded) {                               // (17 Oct 2026) and decoded, before it is shown
        if (!b._ssDec) b._ssDec = (b.decode ? b.decode() : Promise.resolve()).catch(() => {}).then(() => { b.dataset.ssDecoded = '1'; });
        return;
      }
      imgs.forEach(im => im.classList.remove('is-prev'));
      a.classList.remove('is-shown'); a.classList.add('is-prev');
      b.classList.add('is-shown');
      i = next;
    };
    const start = () => {
      const first = box.querySelector('img');
      imgs = [first].concat(urls.slice(1).map(u => {
        const im = new Image();
        im.alt = ''; im.decoding = 'async'; im.width = first.width; im.height = first.height; im.src = u;
        // decoded as soon as it is in, so its turn is never held up (17 Oct 2026)
        im.addEventListener('load', () => { im._ssDec = (im.decode ? im.decode() : Promise.resolve()).catch(() => {}).then(() => { im.dataset.ssDecoded = '1'; }); }, { once: true });
        box.appendChild(im);
        return im;
      }));
      clock = setInterval(step, 2400);
    };
    if ('IntersectionObserver' in window) new IntersectionObserver(es => { seen = es[0].isIntersecting; }).observe(h1);
    const later = () => setTimeout(start, 1200);
    if (document.readyState === 'complete') later(); else window.addEventListener('load', later, { once: true });
  })();

  /* ------------------------------------------------------------ the panel (panel-v1)
     Opened by "Personalize", the pill on the book and the sticky bar. A bottom sheet on a phone (the
     close button, a swipe down on its head or from the top of its content, Escape), a drawer from the
     right on a desktop. Focus stays inside while it is open and goes back where it was after. */
  const panel = root.querySelector('[data-panel]');
  const panelSheet = root.querySelector('[data-panel-sheet]');
  const panelBody = root.querySelector('[data-panel-body]');
  const panelBook = root.querySelector('[data-panel-book]');
  const panelFront = root.querySelector('[data-panel-book] img[data-view]');
  const coverLine = root.querySelector('[data-panel-coverline]');
  let panelFocus = null, panelShut = 0, panelSvg = null;
  const panelIsOpen = () => !!(panel && !panel.hidden && panel.classList.contains('is-open'));
  // Their cover, live: the instant cover the moment the name changes (~0.2s), the real render fading
  // in over it as soon as the front for exactly this name has loaded.
  function panelCover() {
    if (!panel || panel.hidden || !panelBook) return;
    const s = spec(), name = (s.first + ' ' + s.last).trim();
    const real = !!(panelFront && name && panelFront.dataset.swapped === '1' && coverFor === name &&
      panelFront.complete && panelFront.naturalWidth > 0 && !panelFront.classList.contains('is-swapping'));
    if (!name || real) {
      if (panelSvg) panelSvg.classList.add('is-out');
      if (coverLine) coverLine.textContent = name ? '' : 'Their name goes here';
      return;
    }
    if (instant) {
      if (!panelSvg) { panelSvg = instantCover(); panelBook.appendChild(panelSvg); }
      panelSvg.classList.remove('is-out');
      instantName(panelSvg, name);
    }
    if (coverLine) coverLine.textContent = 'Setting their cover…';
  }
  // after the swap's own load handler has cleared is-swapping (it is added later, so it runs later)
  if (panelFront) ['load', 'error'].forEach(t => panelFront.addEventListener(t, () => setTimeout(panelCover)));
  if (f.first) f.first.addEventListener('input', panelCover);
  if (f.last) f.last.addEventListener('input', panelCover);
  const touchFirst = () => window.matchMedia('(hover: none), (pointer: coarse)').matches;
  /* Their cover in sight while a name is typed (15 Oct 2026, Tom's iPhone: the keyboard covered it). On a phone,
     while First or Last name has the focus, the cover is pinned small at the top of the sheet (ss-product.css
     .is-typing) and the field sits just under it. With the keyboard up, the panel takes the part of the screen
     still visible (the visual viewport), so iOS has no reason to slide the sheet up under the bars. */
  const vv = window.visualViewport;
  const phoneNow = () => !window.matchMedia('(min-width: 990px)').matches;
  function kbSync() {
    if (!panel) return;
    const kb = !!(vv && !panel.hidden && phoneNow() && window.innerHeight - vv.height > 120);
    panel.classList.toggle('is-kb', kb);
    panel.style.top = kb ? Math.round(vv.offsetTop) + 'px' : '';
    panel.style.height = kb ? Math.round(vv.height) + 'px' : '';
    panel.style.bottom = kb ? 'auto' : '';
    panel.style.setProperty('--ss-vvh', kb ? Math.round(vv.height) + 'px' : '');
    // only a change of room (the keyboard) brings the field back into view: a scroll of their own never fights back
    const room = kb + ':' + Math.round(vv ? vv.height : window.innerHeight);
    if (room !== lastRoom) { lastRoom = room; typingSync(); if (phoneMobile()) requestAnimationFrame(fieldCentre); }
  }
  // (22 Oct 2026) phones up to 749px: the focused field (with its label) in the middle of the panel's scrolling
  // body, the Preview button in the foot under it. With visualViewport the panel is already the part of the screen
  // above the keyboard (kbSync), so the body is the room to centre in; without it, the browser's own scrollIntoView.
  function fieldCentre() {
    const el = document.activeElement;
    if (!panel || panel.hidden || !phoneMobile() || !el || !panel.contains(el) || !el.matches('input, textarea')) return;
    const box = el.closest('.ss-field') || el;
    if (vv && panelBody && panelBody.scrollHeight > panelBody.clientHeight) {
      const b = panelBody.getBoundingClientRect(), r = box.getBoundingClientRect();
      panelBody.scrollTop += (r.top + r.height / 2) - (b.top + b.height / 2);
    } else if (box.scrollIntoView) {
      box.scrollIntoView({ block: 'center' });
    }
  }
  if (panel) panel.addEventListener('focusin', () => {
    if (!phoneMobile()) return;
    requestAnimationFrame(fieldCentre);
    setTimeout(() => { kbSync(); fieldCentre(); }, 350);          // the keyboard is up by now (iOS, Android, in-app browsers)
  });
  let lastRoom = '';
  // (16 Oct 2026, round 4: on the iPhone the field slid partly behind the pinned cover.) The cover no longer floats
  // over the form: while typing it takes its own room at the top of the panel and only the form scrolls, under it
  // (ss-product.css .is-typing), so a field can never sit behind it. The focused field is then brought fully into
  // the form's view, on focus and again whenever the keyboard changes the room.
  const panelForm = root.querySelector('[data-panel] form');
  function fieldIntoView() {
    const el = document.activeElement;
    if (!panelForm || !el || !panelForm.contains(el)) return;
    const box = panelForm.getBoundingClientRect(), r = el.getBoundingClientRect(), pad = 12;
    const label = el.closest('.ss-field') ? el.closest('.ss-field').getBoundingClientRect().top : r.top;
    if (label < box.top + pad) panelForm.scrollTop -= box.top + pad - label;
    else if (r.bottom > box.bottom - pad) panelForm.scrollTop += r.bottom - (box.bottom - pad);
  }
  // the cover's height while typing: 40% of the width when that fits, else whatever the sheet has left once the
  // head and one field with its label (about 100px) are served, so the field being typed in always fits
  function coverRoom() {
    if (!panelSheet) return;
    const head = panelSheet.querySelector('.ss-panel__head');
    const foot = panelSheet.querySelector('.ss-panel__foot');
    const left = panelSheet.clientHeight - (head ? head.offsetHeight : 64) - (foot ? foot.offsetHeight : 0) - 16 - 104;
    const h = Math.max(72, Math.min(window.innerWidth * 0.4 * 1.5, left));
    panel.style.setProperty('--ss-book-h', Math.round(h) + 'px');
  }
  window.addEventListener('resize', () => { if (panel && panel.classList.contains('is-typing')) { coverRoom(); requestAnimationFrame(fieldIntoView); } });
  function typingSync() {
    if (!panel) return;
    const on = phoneNow() && !phoneMobile() && !panel.hidden && namesFocused();   // no cover to pin on a phone (22 Oct 2026)
    const was = panel.classList.contains('is-typing');
    if (on === was) { if (on) { coverRoom(); requestAnimationFrame(fieldIntoView); } return; }
    const el = document.activeElement;
    panel.classList.toggle('is-typing', on);
    if (on) coverRoom();
    if (on) {
      if (panelBody) panelBody.scrollTop = 0;                // the cover at the top, in full
      if (panelForm) panelForm.scrollTop = 0;
      requestAnimationFrame(() => { fieldIntoView(); requestAnimationFrame(fieldIntoView); });
    } else if (el && panel.contains(el) && el.scrollIntoView) {
      requestAnimationFrame(() => el.scrollIntoView({ block: 'nearest' }));   // back in one scroll, where they were
    }
  }
  if (vv) { vv.addEventListener('resize', kbSync); vv.addEventListener('scroll', kbSync); }
  [f.first, f.last].forEach(el => {
    if (!el) return;
    el.addEventListener('focus', () => { typingSync(); setTimeout(kbSync, 350); });
    el.addEventListener('blur', () => setTimeout(typingSync));
  });
  // Round 8: the desktop panel's right column says when there is more below it
  const moreCue = () => {
    if (!panelForm) return;
    const more = window.matchMedia('(min-width: 1024px)').matches && panelForm.scrollHeight - panelForm.clientHeight - panelForm.scrollTop > 8;
    panelForm.classList.toggle('has-more', more);
  };
  if (panelForm) { panelForm.addEventListener('scroll', moreCue, { passive: true }); panelForm.addEventListener('input', moreCue); window.addEventListener('resize', moreCue); }
  function panelOpen(from) {
    if (!panel) return;
    clearTimeout(panelShut);
    if (ed !== PERSONAL) { ed = PERSONAL; update(); }
    if (buyStatus) { buyStatus.textContent = ''; buyStatus.classList.remove('is-bad'); }
    const already = !panel.hidden;
    if (!already) panelFocus = document.activeElement;
    panel.hidden = false;
    lock('panel');
    // the plain front, only now: nothing in the panel is fetched before it is opened
    if (panelFront && panelFront.dataset.ssSrc !== undefined && !panelFront.getAttribute('src') && !phoneMobile()) {
      panelFront.src = panelFront.dataset.ssSrc; delete panelFront.dataset.ssSrc;
    }
    void panel.offsetWidth;                                  // start from the closed position, then slide in
    panel.classList.add('is-open');
    requestAnimationFrame(moreCue);
    hello();
    panelCover();
    gate();
    if (!already) clarityEvent('Opened panel', ['panel_from', from || 'button']);
    // a desktop goes straight to the first name; a phone keeps the keyboard down so the cover is seen first, unless
    // the home hero sent them here to type it (?personalize=1)
    if (f.first && (from === 'home' || !touchFirst()) && !f.first.value) f.first.focus({ preventScroll: true });
    else (root.querySelector('[data-panel] .ss-panel__close') || panelSheet).focus({ preventScroll: true });
  }
  function panelClose(quiet) {
    if (!panel || panel.hidden) return;
    panel.classList.remove('is-open');
    if (panelSheet) panelSheet.style.transform = '';
    clearTimeout(panelShut);
    panelShut = setTimeout(() => { panel.hidden = true; }, calm ? 0 : 320);
    unlock('panel');
    panel.classList.remove('is-typing');
    kbSync();
    if (phoneMobile() && spec().copy_type !== UNNAMED && spec().first && JSON.stringify(spec()) !== shownKey) schedule(50);
    if (!quiet && panelFocus && panelFocus.focus && document.contains(panelFocus)) panelFocus.focus({ preventScroll: true });
  }
  root.querySelectorAll('[data-panel-open]').forEach(b => b.addEventListener('click', e => { e.stopPropagation(); panelOpen(b.dataset.panelOpen); }));
  if (panel) panel.querySelectorAll('[data-panel-close]').forEach(b => b.addEventListener('click', () => panelClose()));
  // Swipe down to close (phones): from the head, or from the top of the content when it is scrolled to the top.
  if (panel && panelSheet) {
    let y0 = 0, dy = 0, dragging = false, armed = false, t0 = 0;
    panelSheet.addEventListener('touchstart', e => {
      if (!phoneNow() || e.touches.length !== 1) { armed = false; return; }
      // (round 5, Tom's iPhone: scrolling the form closed the sheet) only the grab handle and the head start a
      // swipe, never the form or the cover, and never while the form is scrolled away from its top
      const inHead = !!e.target.closest('[data-panel-grip]') && !e.target.closest('button');
      const scrolled = (panelBody && panelBody.scrollTop > 0) || (panelForm && panelForm.scrollTop > 0);
      armed = inHead && !scrolled;
      y0 = e.touches[0].clientY; dy = 0; dragging = false; t0 = Date.now();
    }, { passive: true });
    panelSheet.addEventListener('touchmove', e => {
      if (!armed) return;
      dy = e.touches[0].clientY - y0;
      if (!dragging) {
        if (dy > 8) { dragging = true; panel.classList.add('is-dragging'); }
        else if (dy < -4) { armed = false; return; }           // an upward move: the content scrolls as usual
        else return;
      }
      e.preventDefault();
      panelSheet.style.transform = 'translateY(' + Math.max(0, dy) + 'px)';
    }, { passive: false });
    const end = () => {
      if (!dragging) { armed = false; return; }
      panel.classList.remove('is-dragging');
      // a decisive pull: past 120px, or a quick flick (60px inside a quarter second); otherwise it springs back
      const fast = dy > 60 && Date.now() - t0 < 250;
      if (dy > 120 || fast) panelClose(); else panelSheet.style.transform = '';
      dragging = false; armed = false;
    };
    panelSheet.addEventListener('touchend', end);
    panelSheet.addEventListener('touchcancel', end);
  }

  /* Focus stays inside the top layer (the bag, else the review sheet, else the panel). */
  function trapTab(e) {
    const top = (bag && !bag.hidden) ? bag : sheetIsOpen() ? sheet : panelIsOpen() ? panel : null;
    if (!top) return;
    const els = Array.from(top.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'))
      .filter(el => el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
    if (!els.length) { e.preventDefault(); return; }
    const first = els[0], last = els[els.length - 1];
    if (!top.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
    else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* ------------------------------------------------------------ the Library edition (panel-v1)
     "Prefer it without a name? Choose the Library edition": no name, no panel; the button
     becomes Add to cart. "Add their name instead" goes back. */
  const classicBtn = root.querySelector('[data-classic]');
  const unclassicBtn = root.querySelector('[data-unclassic]');
  if (classicBtn) classicBtn.addEventListener('click', () => { ed = LIBRARY; update(); if (classicAdd) classicAdd.focus({ preventScroll: true }); });
  if (unclassicBtn) unclassicBtn.addEventListener('click', () => { ed = PERSONAL; update(); if (personalizeBtn) personalizeBtn.focus({ preventScroll: true }); });

  /* ------------------------------------------------------------ the sticky bar (panel-v1, phones)
     Shown once the photograph is out of view; never with the Personalize button (or the Library edition's Add to
     cart) on screen, never under a layer, never while Shopify's cookie banner is up. It moves clear of
     a chat button in the bottom right corner if the shop ever adds one. */
  const bar = root.querySelector('[data-stickybar]');
  const phoneMQ = window.matchMedia('(max-width: 989px)');
  let heroIn = true, ctaIn = false;
  const visible = el => { if (!el) return false; const r = el.getBoundingClientRect(); return r.width > 0 && r.height > 0 && getComputedStyle(el).visibility !== 'hidden' && getComputedStyle(el).display !== 'none'; };
  const CHAT = '#shopify-chat, inbox-online-store-chat, #ShopifyChat, #gorgias-chat-container, #tidio-chat, #launcher, .intercom-lightweight-app-launcher, #chat-widget-container';
  function chatClear() {
    let clear = 0;
    document.querySelectorAll(CHAT).forEach(el => {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.width < 200 && r.height < 200 && r.bottom > window.innerHeight - 140 && r.left > window.innerWidth / 2) clear = Math.max(clear, window.innerWidth - r.left + 12);
    });
    bar.classList.toggle('has-chat', clear > 0);
    if (clear) bar.style.setProperty('--ss-chat-clear', Math.round(clear) + 'px');
  }
  function stickySync() {
    if (!bar) return;
    const banner = visible(document.getElementById('shopify-pc__banner'));
    const show = phoneMQ.matches && !heroIn && !ctaIn && !locks.size && !banner;
    bar.classList.toggle('is-shown', show);
    document.body.classList.toggle('has-ss-stickybar', show);
    if (show) chatClear();
  }
  if (bar && 'IntersectionObserver' in window) {
    const stage = root.querySelector('[data-track]');
    const cta = root.querySelector('[data-cta]');
    if (stage) new IntersectionObserver(es => { heroIn = es[0].isIntersecting; stickySync(); }).observe(stage);
    if (cta) new IntersectionObserver(es => { ctaIn = es[es.length - 1].isIntersecting; stickySync(); }).observe(cta);
    let raf = 0;
    window.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(stickySync); }, { passive: true });
    if (phoneMQ.addEventListener) phoneMQ.addEventListener('change', stickySync);
  }

  // ?edition=library or ?edition=personalized chooses the edition (12 Oct 2026: the ads link
  // with ?edition=personalized; Personalized is also the page's own default now).
  (function editionParam() {
    try {
      const e = new URLSearchParams(location.search).get('edition');
      if (e === 'library' || e === 'personalized') ed = e === 'library' ? LIBRARY : PERSONAL;
    } catch (err) { /* no URL API: the page's default stands */ }
  })();
  // Personalized is the default, and it renders nothing until a name is typed, unless
  // the home hero sent a name (Tom, 27 Sept): its
  // "Order <title> for <name>" link carries ?edition=personalized&for=&from= in the
  // URL, and "or choose another classic" leaves { for, from } in this tab's
  // sessionStorage ("ss-carry"). Either opens the Personalized edition with First
  // name, Surname (the rest of the name) and Sign-off filled in. Fields the
  // customer has already typed in are never overwritten.
  (function carryIn() {
    let c = null;
    try {
      const q = new URLSearchParams(location.search);
      if (q.get('edition') === 'personalized') c = { for: q.get('for') || '', from: q.get('from') || '' };
    } catch (e) { /* no URL API: nothing to carry */ }
    if (!c) { try { c = JSON.parse(sessionStorage.getItem('ss-carry') || 'null'); } catch (e) { c = null; } }
    if (!c || !String(c.for || '').trim()) return;
    const parts = String(c.for).trim().split(/\s+/);
    ed = PERSONAL;
    if (f.first && !f.first.value) f.first.value = parts.shift().slice(0, f.first.maxLength > 0 ? f.first.maxLength : 24);
    if (f.last && !f.last.value && parts.length) f.last.value = parts.join(' ').slice(0, f.last.maxLength > 0 ? f.last.maxLength : 24);
    // the sign-off starts with the default now (panel-v1): a "from" sent from the home hero replaces it, never typed words
    if (f.signed_off && c.from && f.signed_off.value.trim() === (f.signed_off.dataset.default || '').trim()) f.signed_off.value = String(c.from).slice(0, f.signed_off.maxLength > 0 ? f.signed_off.maxLength : 60);
  })();
  if (f.dedication && !dedTouched) f.dedication.value = dedDefault();   // a carried-in first name reaches a [first name] default
  if (f.dedication && count) count.textContent = f.dedication.value.length + ' / ' + (f.dedication.maxLength || 320);
  preSync();
  update();
  // ?personalize=1 no longer opens the panel on load (17 Oct 2026): ad visitors landing inside the open panel closed it
  // and left. The parameter is ignored; the page opens closed and the Personalize button or the "Try their name" pill opens it.
  // (22 Oct 2026) The home hero's "Try their name" pill (and, on a phone, its book) sends ?personalize=home: that one
  // visitor has just asked to try a name, so the panel opens, once; the parameter is taken off the address so a reload
  // or Back does not open it again. Any other value (an ad's ?personalize=1) still opens the page closed.
  try {
    const q = new URLSearchParams(location.search);
    if (q.get('personalize') === 'home') {
      q.delete('personalize');
      history.replaceState(history.state, '', location.pathname + (q.toString() ? '?' + q : '') + location.hash);
      if (ed === PERSONAL) panelOpen('home');
    }
  } catch (e) { /* no URL API: the page opens closed */ }
})();
